/**
 * On-demand demo seeding for the prototype "Seed data" button.
 *
 * Each call returns a fresh, internally-consistent driver + task: a random name
 * and real-city freight lane (so the map geocodes), paired with one of several
 * scenarios that wire up matching risk, HOS, tags, and a suggested message.
 */

import type {
  AppointmentInfo,
  DispatchTask,
  Driver,
  DriverStatusCode,
  HosInfo,
  LoadType,
  RiskInfo,
  RouteModel,
  TagKind,
  TaskType,
  Urgency,
  VelocityTrend,
} from "../types";

const NAMES = [
  "Diego Ramirez",
  "Tanya Brooks",
  "Earl Jennings",
  "Priya Nair",
  "Cole Whitfield",
  "Marisol Vega",
  "Andre Foster",
  "Hank Delgado",
  "Latoya Simmons",
  "Wes Carmichael",
  "Bianca Ortiz",
  "Sam Okafor",
];

const LANES = [
  { origin: "Dallas, TX", destination: "Atlanta, GA", location: "I-20 near Meridian, MS" },
  { origin: "Phoenix, AZ", destination: "Los Angeles, CA", location: "I-10 near Indio, CA" },
  { origin: "Chicago, IL", destination: "Indianapolis, IN", location: "I-65 near Lafayette, IN" },
  { origin: "Nashville, TN", destination: "Memphis, TN", location: "I-40 near Jackson, TN" },
  { origin: "Kansas City, MO", destination: "Denver, CO", location: "I-70 near Salina, KS" },
  { origin: "Houston, TX", destination: "San Antonio, TX", location: "I-10 near Seguin, TX" },
  { origin: "Columbus, OH", destination: "Pittsburgh, PA", location: "I-70 near Wheeling, WV" },
  { origin: "Salt Lake City, UT", destination: "Las Vegas, NV", location: "I-15 near Cedar City, UT" },
];

const CUSTOMERS = [
  { name: "Walmart DC 6094", city: "Sanger, TX" },
  { name: "Kroger DC", city: "Memphis, TN" },
  { name: "Target DC T-3808", city: "Tucson, AZ" },
  { name: "Amazon MEM1", city: "Memphis, TN" },
  { name: "Home Depot RDC", city: "Dallas, TX" },
  { name: "Costco Depot", city: "Mira Loma, CA" },
  { name: "Dollar General DC", city: "Ardmore, OK" },
  { name: "Publix DC", city: "Lakeland, FL" },
];

const DIVISIONS = [
  { code: "100", label: "OTR" },
  { code: "220", label: "Dedicated" },
  { code: "410", label: "Regional" },
];

interface ScenarioCtx {
  name: string;
  customer: string;
}

interface Scenario {
  type: TaskType;
  urgency: Urgency;
  status: DriverStatusCode;
  statusLabel: string;
  loadType: LoadType;
  velocityMph: number;
  velocityTrend: VelocityTrend;
  heavyTraffic: boolean;
  tags: TagKind[];
  appointment: AppointmentInfo;
  appointmentRisk?: RiskInfo;
  /** Projected appointment arrival (HH:MM). When set, PTA is derived as arrival + 2h. */
  apptArrival?: string;
  /** Fallback projected PTA when there's no appointment arrival (e.g. empty driver). */
  ptaTime?: string;
  ptaRisk: RiskInfo;
  hos: HosInfo;
  summary: (c: ScenarioCtx) => string;
  recommendation: string;
  cannedMessage: (c: ScenarioCtx) => string;
}

const SCENARIOS: Scenario[] = [
  {
    type: "pta_risk",
    urgency: "urgent",
    status: "T",
    statusLabel: "In transit",
    loadType: "Live unload",
    velocityMph: 9,
    velocityTrend: "down",
    heavyTraffic: true,
    tags: ["pta", "traffic"],
    appointment: { kind: "window", start: "13:00", end: "15:00" },
    appointmentRisk: {
      atRisk: true,
      projected: "15:20",
      target: "Window ends 15:00",
      delta: "+20m",
      reason: "Traffic",
    },
    apptArrival: "15:20",
    ptaRisk: { atRisk: true, projected: "17:20", target: "Planned 16:30", delta: "+50m", reason: "HOS + Traffic" },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "3h 05m left",
      primaryContext: "vs 2h 40m to appt",
      clocks: [
        { label: "Drive", value: "3h 05m" },
        { label: "Shift", value: "5h 20m" },
        { label: "Break", value: "6h 30m" },
        { label: "Cycle", value: "28h 40m" },
      ],
    },
    summary: (c) => `${c.name} is slowing in heavy traffic and projected to miss the ${c.customer} delivery window.`,
    recommendation: "Notify the receiver of a ~20 min delay and confirm late-arrival policy.",
    cannedMessage: (c) =>
      `Hi ${c.name.split(" ")[0]}, traffic is heavy ahead — please confirm your updated ETA to ${c.customer} so we can notify the receiver.`,
  },
  {
    type: "empty_call",
    urgency: "high",
    status: "AC",
    statusLabel: "Available",
    loadType: "Empty",
    velocityMph: 0,
    velocityTrend: "flat",
    heavyTraffic: false,
    tags: ["empty_call"],
    appointment: { kind: "window", start: "08:00", end: "12:00" },
    ptaTime: "Now",
    ptaRisk: { atRisk: false, projected: "Now", target: "Planned now", delta: "On time" },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "10h 30m left",
      primaryContext: "full clock",
      clocks: [
        { label: "Drive", value: "10h 30m" },
        { label: "Shift", value: "13h 00m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "52h 10m" },
      ],
    },
    summary: (c) => `${c.name} has been empty and available for 2h with no next load assigned.`,
    recommendation: "Assign the next load or confirm the driver called in empty.",
    cannedMessage: (c) =>
      `Hi ${c.name.split(" ")[0]}, confirming you're empty and ready — we're working your next load now and will send details shortly.`,
  },
  {
    type: "late_appt",
    urgency: "urgent",
    status: "T",
    statusLabel: "In transit",
    loadType: "Drop",
    velocityMph: 62,
    velocityTrend: "flat",
    heavyTraffic: false,
    tags: ["pta"],
    appointment: { kind: "strict", time: "14:00" },
    appointmentRisk: {
      atRisk: true,
      projected: "14:35",
      target: "Appointment time 14:00",
      delta: "+35m",
      reason: "HOS",
    },
    apptArrival: "14:35",
    ptaRisk: { atRisk: true, projected: "16:35", target: "Planned 16:00", delta: "+35m", reason: "HOS" },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "0h 40m left",
      primaryContext: "vs 1h 10m to appt",
      clocks: [
        { label: "Drive", value: "0h 40m" },
        { label: "Shift", value: "1h 15m" },
        { label: "Break", value: "3h 20m" },
        { label: "Cycle", value: "12h 05m" },
      ],
    },
    summary: (c) => `${c.name} will run out of drive hours before reaching the strict ${c.customer} appointment.`,
    recommendation: "Reschedule the appointment or arrange relay; driver can't legally make 14:00.",
    cannedMessage: (c) =>
      `Hi ${c.name.split(" ")[0]}, you're tight on hours for the 14:00 at ${c.customer}. Hold at the next safe stop — we're rebooking the appointment.`,
  },
  {
    type: "split_sleeper",
    urgency: "med",
    status: "T",
    statusLabel: "In transit",
    loadType: "Live unload",
    velocityMph: 58,
    velocityTrend: "flat",
    heavyTraffic: false,
    tags: ["split_sleeper"],
    appointment: { kind: "window", start: "06:00", end: "10:00" },
    apptArrival: "07:30",
    ptaRisk: { atRisk: false, projected: "09:30", target: "Planned 09:30", delta: "On time" },
    hos: {
      primaryLabel: "Break",
      primaryValue: "1h 50m left",
      primaryContext: "split 7/3 eligible",
      clocks: [
        { label: "Drive", value: "4h 20m" },
        { label: "Shift", value: "5h 00m" },
        { label: "Break", value: "1h 50m" },
        { label: "Cycle", value: "31h 15m" },
      ],
    },
    summary: (c) => `${c.name} can split-sleeper to protect hours and still hit the ${c.customer} morning window.`,
    recommendation: "Remind driver to take the 7-hour split now to preserve drive time.",
    cannedMessage: (c) =>
      `Hi ${c.name.split(" ")[0]}, take your 7-hour split at the next stop — it keeps you legal for the morning delivery at ${c.customer}.`,
  },
  {
    type: "home_time",
    urgency: "high",
    status: "T",
    statusLabel: "In transit",
    loadType: "Drop",
    velocityMph: 64,
    velocityTrend: "flat",
    heavyTraffic: false,
    tags: ["home_time"],
    appointment: { kind: "window", start: "16:00", end: "18:00" },
    apptArrival: "16:30",
    ptaRisk: { atRisk: true, projected: "18:30", target: "Planned 17:00", delta: "+1h 30m", reason: "HOS" },
    hos: {
      primaryLabel: "Cycle",
      primaryValue: "6h 10m left",
      primaryContext: "RDO starts tomorrow",
      clocks: [
        { label: "Drive", value: "5h 30m" },
        { label: "Shift", value: "7h 45m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "6h 10m" },
      ],
    },
    summary: (c) => `${c.name} has a home-time request that conflicts with the current ${c.customer} delivery plan.`,
    recommendation: "Confirm home-time window and re-plan the load so the driver gets home on schedule.",
    cannedMessage: (c) =>
      `Hi ${c.name.split(" ")[0]}, we see your home-time request — we're re-planning the ${c.customer} load to get you home on time. Confirm your target.`,
  },
  {
    type: "empty_call",
    urgency: "high",
    status: "T",
    statusLabel: "At customer",
    loadType: "Live unload",
    velocityMph: 0,
    velocityTrend: "flat",
    heavyTraffic: false,
    tags: ["empty_call"],
    appointment: { kind: "window", start: "12:00", end: "15:00" },
    appointmentRisk: { atRisk: false, projected: "13:00", target: "Window ends 15:00" },
    apptArrival: "13:00",
    ptaRisk: { atRisk: false, projected: "15:00", target: "Planned 15:00", delta: "On time" },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "6h 40m left",
      clocks: [
        { label: "Drive", value: "6h 40m" },
        { label: "Shift", value: "8h 30m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "40h 15m" },
      ],
    },
    summary: (c) => `${c.name} manually arrived at the call — the loaded call may need manual processing.`,
    recommendation: "Check to see if the loaded call needs to be processed manually.",
    cannedMessage: (c) =>
      `Hi ${c.name.split(" ")[0]}, we see you've arrived — confirming the loaded call so we can process it. Anything we should know?`,
  },
  {
    type: "empty_call",
    urgency: "high",
    status: "T",
    statusLabel: "At customer",
    loadType: "Live unload",
    velocityMph: 0,
    velocityTrend: "flat",
    heavyTraffic: false,
    tags: ["empty_call"],
    appointment: { kind: "window", start: "09:00", end: "12:00" },
    appointmentRisk: { atRisk: false, projected: "10:00", target: "Window ends 12:00" },
    apptArrival: "10:00",
    ptaRisk: { atRisk: false, projected: "12:00", target: "Planned 12:00", delta: "On time" },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "7h 10m left",
      clocks: [
        { label: "Drive", value: "7h 10m" },
        { label: "Shift", value: "9h 00m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "45h 30m" },
      ],
    },
    summary: (c) => `Invalid trailer number detected on ${c.name}'s loaded call.`,
    recommendation: "Check trailer number and trailer location for loaded call.",
    cannedMessage: (c) =>
      `Hi ${c.name.split(" ")[0]}, the trailer number on your loaded call looks off — can you confirm the trailer number and where it's parked?`,
  },
  {
    type: "empty_call",
    urgency: "high",
    status: "T",
    statusLabel: "At customer",
    loadType: "Empty",
    velocityMph: 0,
    velocityTrend: "flat",
    heavyTraffic: false,
    tags: ["empty_call"],
    appointment: { kind: "window", start: "08:00", end: "12:00" },
    appointmentRisk: { atRisk: false, projected: "09:30", target: "Window ends 12:00" },
    apptArrival: "09:30",
    ptaRisk: { atRisk: false, projected: "11:30", target: "Planned 11:30", delta: "On time" },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "8h 05m left",
      clocks: [
        { label: "Drive", value: "8h 05m" },
        { label: "Shift", value: "10h 20m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "48h 50m" },
      ],
    },
    summary: (c) => `Invalid trailer number detected — ${c.name} entered N/A on the unloaded call.`,
    recommendation: "Process unloaded call manually. Send preplan for next load.",
    cannedMessage: (c) =>
      `Hi ${c.name.split(" ")[0]}, your next preplan is on the way — we're processing the unloaded call now. Stand by for load details.`,
  },
  {
    type: "pta_risk",
    urgency: "med",
    status: "T",
    statusLabel: "In transit",
    loadType: "Live unload",
    velocityMph: 61,
    velocityTrend: "flat",
    heavyTraffic: false,
    tags: ["pta"],
    appointment: { kind: "window", start: "14:00", end: "16:00" },
    appointmentRisk: { atRisk: false, projected: "14:30", target: "Window ends 16:00" },
    apptArrival: "14:30",
    ptaRisk: { atRisk: false, projected: "16:30", target: "Planned 16:30", delta: "On time" },
    hos: {
      primaryLabel: "Drive",
      primaryValue: "5h 25m left",
      clocks: [
        { label: "Drive", value: "5h 25m" },
        { label: "Shift", value: "7h 40m" },
        { label: "Break", value: "8h 00m" },
        { label: "Cycle", value: "36h 05m" },
      ],
    },
    summary: (c) => `Auto dispatch detected for ${c.name}'s appointment window.`,
    recommendation: "Confirm PTA and push to AS400.",
    cannedMessage: (c) =>
      `Hi ${c.name.split(" ")[0]}, confirming your appointment window and PTA — let us know if anything changes.`,
  },
];

const pick = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

/** Add hours to an HH:MM time, wrapping at midnight. Non-time strings pass through. */
function addHours(time: string, hours: number): string {
  const match = /^(?<h>\d{1,2}):(?<m>\d{2})$/.exec(time);
  if (!match?.groups) return time;
  const total = (Number(match.groups.h) * 60 + Number(match.groups.m) + hours * 60) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function makeRoute(origin: string, destination: string, heavyTraffic: boolean): RouteModel {
  const segments = heavyTraffic
    ? [
        { level: "clear" as const, weight: 3 },
        { level: "moderate" as const, weight: 1 },
        { level: "heavy" as const, weight: 1.6 },
        { level: "moderate" as const, weight: 1 },
        { level: "clear" as const, weight: 2 },
      ]
    : [
        { level: "clear" as const, weight: 4 },
        { level: "moderate" as const, weight: 1.2 },
        { level: "clear" as const, weight: 3 },
      ];
  return { origin, destination, segments, cities: [] };
}

let seq = 0;

/** Build one fresh demo driver + suggested task for the "Seed data" button. */
export function makeSeededEntry(): { driver: Driver; task: DispatchTask } {
  seq += 1;
  const id = `seed_${Date.now()}_${seq}`;
  const name = pick(NAMES);
  const truck = String(1000 + Math.floor(Math.random() * 8999));
  const lane = pick(LANES);
  const customer = pick(CUSTOMERS);
  const division = pick(DIVISIONS);
  const scenario = pick(SCENARIOS);
  const ctx: ScenarioCtx = { name, customer: customer.name };

  // Drivers typically get a 2h unload window, so PTA = projected appointment arrival + 2h.
  const projectedPta = scenario.apptArrival ? addHours(scenario.apptArrival, 2) : (scenario.ptaTime ?? "—");
  const ptaRisk: RiskInfo = scenario.apptArrival ? { ...scenario.ptaRisk, projected: projectedPta } : scenario.ptaRisk;

  const driver: Driver = {
    id,
    name,
    truck,
    division: division.code,
    divisionLabel: division.label,
    status: scenario.status,
    statusLabel: scenario.statusLabel,
    customer: customer.name,
    customerCity: customer.city,
    location: lane.location,
    velocityMph: scenario.velocityMph,
    velocityTrend: scenario.velocityTrend,
    fuelLevel: 15 + Math.floor(Math.random() * 81),
    loadType: scenario.loadType,
    destination: lane.destination,
    appointment: scenario.appointment,
    nextStop: `Receiver appt — ${customer.name}`,
    appointmentRisk: scenario.appointmentRisk,
    pta: { time: projectedPta, risk: ptaRisk },
    hos: scenario.hos,
    tags: scenario.tags,
    route: makeRoute(lane.origin, lane.destination, scenario.heavyTraffic),
  };

  const task: DispatchTask = {
    id: `${id}:task`,
    driverId: id,
    type: scenario.type,
    state: "suggested",
    urgency: scenario.urgency,
    summary: scenario.summary(ctx),
    recommendation: scenario.recommendation,
    cannedMessage: scenario.cannedMessage(ctx),
  };

  return { driver, task };
}
